"use client";

import React from "react";
import { motion } from "framer-motion";
import { Testimonial } from "./testimonial";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";

function parseScopeHeadline(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(Efficace|Adapté)/i);
    return parts.map((part, i) => {
      if (/Efficace/i.test(part)) {
        return <span key={i} className="italic text-gold">{part}</span>;
      }
      if (/Adapté/i.test(part)) {
        return <span key={i} className="italic">{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }
  const parts = text.split(/(Effective|Adapted)/i);
  return parts.map((part, i) => {
    if (/Effective/i.test(part)) {
      return <span key={i} className="italic text-gold">{part}</span>;
    }
    if (/Adapted/i.test(part)) {
      return <span key={i} className="italic">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export const ScopePillars = () => {
  const { t, lang } = useV2Translation();

  return (
    <section
      id="engine"
      data-testid="pillars-section"
      className="py-24 lg:py-32 bg-paper-alt border-t border-line"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-16 lg:mb-20">
          <div className="col-span-12">
            <h2
              data-testid="pillars-headline"
              className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight max-w-[24ch]"
            >
              {parseScopeHeadline(t.scopePillars.headline, lang)}
            </h2>
          </div>
        </div>

        <div
          className="border-t border-l border-ink/20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
          style={{ perspective: 1200 }}
        >
          {t.scopePillars.pillars.map((p, i) => {
            const num = String(i + 1).padStart(2, "0");
            return (
              <motion.article
                key={p.title}
                data-testid={`pillar-card-${i + 1}`}
                initial={{ opacity: 0, y: 40, rotateX: -8 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.7,
                  delay: (i % 4) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                style={{ transformStyle: "preserve-3d" }}
                className="group relative border-r border-b border-ink/20 p-8 lg:p-10 bg-paper-pure hover:bg-navy hover:text-paper transition-colors duration-300 flex flex-col min-h-[340px] lg:min-h-[400px] overflow-hidden"
              >
                {/* Hover sweep */}
                <span className="absolute inset-x-0 bottom-0 h-0 bg-gold/10 group-hover:h-full transition-all duration-500 ease-out -z-0" />

                <div className="relative flex items-start justify-between mb-10">
                  <span className="font-mono text-xs uppercase tracking-widest text-gold-muted group-hover:text-gold transition-colors">
                    {p.tag}
                  </span>
                  <motion.span
                    className="font-display text-4xl italic leading-none text-ink/30 group-hover:text-gold transition-colors"
                    initial={{ rotate: -4 }}
                    whileHover={{ rotate: 4, scale: 1.15 }}
                    transition={{ type: "spring", stiffness: 280, damping: 14 }}
                  >
                    {num}
                  </motion.span>
                </div>
                <h3 className="relative font-display text-2xl lg:text-[28px] leading-tight tracking-tight mb-5">
                  {p.title}
                </h3>
                <p
                  className="relative font-body text-sm leading-relaxed text-ink/70 group-hover:text-paper/75 transition-colors flex-1"
                  dangerouslySetInnerHTML={{ __html: p.body }}
                />
                <div className="relative mt-8 h-px w-10 bg-gold group-hover:w-24 transition-all duration-500" />
              </motion.article>
            );
          })}
        </div>

        <div className="mt-16 lg:mt-20 grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <Testimonial
              index={2}
              variant="light"
              quoteTitle={t.scopePillars.testimonial2.quoteTitle}
              body={t.scopePillars.testimonial2.body}
              author={t.scopePillars.testimonial2.author}
              role={t.scopePillars.testimonial2.role}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
